import React from 'react';
import { MetaPageTriggerSelector } from './MetaPageTriggerSelector';
import { InstagramBusinessAccount, FacebookPage } from '@/common/reducers/backend-api/backend-api';
import { TriggerSourceDefinition, TriggerData, FacebookTriggerSourceData, InstagramTriggerSourceData } from '@/common/types/trigger.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

interface TriggerSourceConfigProps {
  source: TriggerSourceDefinition;
  triggerData: TriggerData;
  onUpdateTriggerData: (data: Partial<TriggerData>) => void;
}

// Convert a string with spaces to camelCase
// e.g., 'instagram page' becomes 'instagramPage'
const toCamelCase = (str: string): string => {
  return str.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
    return index === 0 ? word.toLowerCase() : word.toUpperCase();
  }).replace(/\s+/g, '');
};

export const TriggerSourceConfig: React.FC<TriggerSourceConfigProps> = ({
  source,
  triggerData,
  onUpdateTriggerData,
}) => {
  const { t } = useTranslation(namespace);
  const triggerSourceKeyMap: Record<string, string> = {
    'blueticks extension': 'blueticksExtension',
    'blueticks assistant': 'blueticksAssistant',
    'customer support': 'customerSupport',
    'instagram page': 'instagramPage',
    'facebook page': 'facebookPage',
  };

  const sourceKey = triggerSourceKeyMap[source.value];
  const sourceLabel = sourceKey
    ? t(`chatbot.trigger.sources.${sourceKey}.label`, { defaultValue: source.label })
    : source.label;

  // Only show configuration if the trigger source requires it
  if (!source?.requiresConfiguration) {
    return null;
  }

  // Block event propagation to prevent clicks inside from triggering parent components
  const blockEvent = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Generic handler for page selection
  const handleMetaPageSelected = (page: FacebookPage | InstagramBusinessAccount | null) => {
    const sourceKey = source.value; // 'instagram page' or 'facebook page'
    let sourceSpecificData: FacebookTriggerSourceData | InstagramTriggerSourceData | undefined = undefined;

    if (page) {
      // Check if it's an Instagram account using a unique property like 'username'
      if ('username' in page) { 
        const igPage = page as InstagramBusinessAccount;
        sourceSpecificData = {
          selectedPageId: igPage.id,
          selectedPageName: igPage.name,
          selectedPageUsername: igPage.username,
          selectedPageProfilePicture: igPage.profile_picture_url,
          messagingEnabled: igPage.messagingEnabled,
          facebookPageId: igPage.facebookPageId,
        };
      } else { // Assume it's a Facebook Page
        const fbPage = page as FacebookPage;
        sourceSpecificData = {
          selectedPageId: fbPage.id,
          selectedPageName: fbPage.name,
          selectedPageProfilePicture: fbPage.profile_picture_url, 
        };
      }
    }

    onUpdateTriggerData({
      triggerSourceData: {
        ...triggerData.triggerSourceData,
        [sourceKey]: sourceSpecificData, // Store under the correct key
      },
    });
  };

  // Determine which selector to render based on configurationType
  const renderSelector = () => {
    const sourceKey = source.value;
    const savedData = triggerData.triggerSourceData?.[sourceKey];
    let savedPage: FacebookPage | InstagramBusinessAccount | null = null;
    
    // Reconstruct savedPage based on type
    if (savedData?.selectedPageId) {
      if (sourceKey === 'instagram page') {
        const igData = savedData as InstagramTriggerSourceData;
        savedPage = {
          id: igData.selectedPageId, 
          name: igData.selectedPageName || 'Unknown', 
          username: igData.selectedPageUsername || 'unknown',
          profile_picture_url: igData.selectedPageProfilePicture,
          messagingEnabled: igData.messagingEnabled,
          facebookPageId: igData.facebookPageId || '',
          // Add defaults
          followers_count: undefined, 
          pageAccessToken: undefined, 
        } as InstagramBusinessAccount;
      } else if (sourceKey === 'facebook page') {
        const fbData = savedData as FacebookTriggerSourceData;
        savedPage = {
          id: fbData.selectedPageId,
          name: fbData.selectedPageName || 'Unknown',
          profile_picture_url: fbData.selectedPageProfilePicture,
          // Add defaults
          hasInstagramAccount: false, 
          accessToken: undefined,
          permissions: undefined,
          instagramAccountId: undefined,
        } as FacebookPage;
      }
    }

    if (source.configurationType === 'instagram_page_selection') {
      return (
        <MetaPageTriggerSelector
          pageType="instagram" // Pass the type
          savedPage={savedPage as InstagramBusinessAccount | null} // Pass reconstructed saved page
          onPageSelected={handleMetaPageSelected}
        />
      );
    } else if (source.configurationType === 'facebook_page_selection') { 
      return ( 
        <MetaPageTriggerSelector
          pageType="facebook" // Pass the type
          savedPage={savedPage as FacebookPage | null} // Pass reconstructed saved page
          onPageSelected={handleMetaPageSelected}
        />
      );
    }
    return null; 
  };

  return (
    <Card className="mt-4 mb-2 bg-slate-50">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-base">{t('chatbot.trigger.configureSource', { source: sourceLabel })}</CardTitle>
      </CardHeader>
      <CardContent className="py-4 px-4">
        {renderSelector()}
      </CardContent>
    </Card>
  );
};

export default TriggerSourceConfig; 
